'use client'
// PARENTS-MENU (README_CURRENT_TASKS §7): the desk's parent-chain navigation —
// the REFERENCE RENDERER of the menu model's `parents` entry. A surface that
// wants a different presentation (breadcrumbs, inline strip) reads
// useDeskMenu().parents and renders its own; mounting this leaf is optional.
//
// SKELETON component: structure + md-* contract classes only; every visual
// class arrives through the ParentsMenuSkin the owning surface passes (no-CSS
// rule, docs/25). The chain comes from the boot payload through the menu
// model (grants=1-gated serializer field), so SSR and hydration agree on
// first paint. The link list is ALWAYS in the DOM — legacy kept the hidden
// list server-rendered and toggled it on hover — only its visibility is
// client state.
//
// Open/close is React-owned (hover, click, Escape, outside click). Navigation
// is a plain <a href> full page load, exactly like legacy _html_parents_ul:
// a parent link is another desk, so the server must re-derive everything.

import { useEffect, useRef, useState } from 'react'
import { useDeskMenu } from './menuModel'

export interface ParentsMenuSkin {
  root?: string
  trigger?: string
  triggerOpen?: string
  count?: string
  list?: string
  link?: string
  homeLink?: string
}

export interface ParentsMenuLabels {
  trigger: string
  home: string
}

export function ParentsMenu({
  skin = {},
  labels = { trigger: 'Parents', home: 'Msgdesk.com' },
  as: Root = 'div',
  excludeMsgType,
  includeMsgType,
  isMsgOnly = false,
}: {
  skin?: ParentsMenuSkin
  labels?: ParentsMenuLabels
  /** Wrapper element — 'li' when the owning surface mounts it in a menu list. */
  as?: 'div' | 'li'
  /** Surface presentation filter; the parent load and shared model stay whole. */
  excludeMsgType?: string
  /** Include only this persisted product type. */
  includeMsgType?: string
  /** Include named-message ancestors only; omit Answer ancestors. */
  isMsgOnly?: boolean
}) {
  const { parents } = useDeskMenu()
  const links = parents.links.filter(link => (
    (!excludeMsgType || link.msgType !== excludeMsgType)
    && (!includeMsgType || link.msgType === includeMsgType)
    && (!isMsgOnly || link.isMsg)
  ))
  const isFiltered = Boolean(excludeMsgType || includeMsgType || isMsgOnly)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | HTMLLIElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDocPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    function onDocKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDocPointerDown)
    document.addEventListener('keydown', onDocKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown)
      document.removeEventListener('keydown', onDocKeyDown)
    }
  }, [open])

  // Legacy rule: the menu exists only when a chain exists (msges needed
  // count_parents > 0; answers always have one) and parents are not hidden
  // (is_hide_parents zeroes the chain server-side) or show-locked.
  if (!parents.canSee || links.length < 1) return null

  return (
    <Root
      ref={rootRef as never}
      className={`md-parents ${skin.root ?? ''}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={`md-parents__trigger ${skin.trigger ?? ''} ${open ? `md-parents__trigger--open ${skin.triggerOpen ?? ''}` : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {labels.trigger}
        <span className={`md-parents__count ${skin.count ?? ''}`}>({isFiltered ? links.length : parents.count})</span>
      </button>
      <div
        className={`md-parents__list ${skin.list ?? ''}`}
        style={{ display: open ? undefined : 'none' }}
      >
        {links.map((link) => (
          <a
            key={link.number}
            className={`md-parents__link ${skin.link ?? ''}`}
            href={`/${link.codeKey}`}
          >
            {link.number}: {link.label}
          </a>
        ))}
        <a className={`md-parents__home ${skin.homeLink ?? ''}`} href="/">
          {labels.home}
        </a>
      </div>
    </Root>
  )
}
